import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type TextareaHTMLAttributes } from 'react';
import { mentionToken, splitMentions, MENTION_PATTERN } from '@vidmakr/shared';
import { Avatar, Textarea, cx } from './ui';

/** Someone who can be @mentioned. */
export interface MentionCandidate {
  userId: string;
  name: string;
  email?: string;
  avatarUrl?: string;
}

export interface PickedMention {
  userId: string;
  name: string;
}

/**
 * Turns what was typed ("… @Ana Lima …") into the stored form, for the people
 * picked from the suggestions. Mentions whose text was edited away are dropped.
 */
export function encodeMentions(text: string, picked: PickedMention[]): string {
  const byName = [...new Map(picked.map((p) => [p.name, p])).values()].sort((a, b) => b.name.length - a.name.length);
  if (!byName.length) return text;
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`@(${byName.map((p) => esc(p.name)).join('|')})(?![\\p{L}\\p{N}_])`, 'gu');
  return text.replace(re, (_m, name: string) => {
    const p = byName.find((x) => x.name === name)!;
    return mentionToken(p.name, p.userId);
  });
}

/** The stored form back to what people type (for editing a comment). */
export function decodeMentions(body: string): { text: string; picked: PickedMention[] } {
  const picked: PickedMention[] = [];
  const text = body.replace(MENTION_PATTERN, (_m, name: string, userId: string) => {
    picked.push({ name, userId });
    return `@${name}`;
  });
  return { text, picked };
}

/** A comment with its mentions highlighted. */
export function CommentBody({ body, meId, className }: { body: string; meId?: string; className?: string }) {
  const parts = useMemo(() => splitMentions(body), [body]);
  return (
    <span className={className}>
      {parts.map((p, i) =>
        'text' in p ? (
          <span key={i}>{p.text}</span>
        ) : (
          <span key={i} className={cx('rounded px-0.5 font-medium', p.userId === meId ? 'bg-accent/25 text-accent-2' : 'text-accent-2')}>
            @{p.mention}
          </span>
        ),
      )}
    </span>
  );
}

export interface MentionTextareaHandle {
  /** The text in stored form (mentions encoded). */
  value(): string;
  /** Replaces the content (stored form). */
  reset(body?: string): void;
  focus(): void;
}

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'defaultValue'> & {
  candidates: MentionCandidate[];
  /** Initial content, in stored form. */
  initial?: string;
  onTextChange?: (text: string) => void;
};

/**
 * A textarea with @mention suggestions. Typing "@" and part of a name lists
 * matching people; picking one inserts "@Name". Read the result through the
 * handle (`value()`), which encodes the picked mentions.
 */
export const MentionTextarea = forwardRef<MentionTextareaHandle, Props>(function MentionTextarea(
  { candidates, initial = '', onTextChange, onKeyDown, className, ...rest },
  ref,
) {
  const start = useMemo(() => decodeMentions(initial), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [text, setText] = useState(start.text);
  const picked = useRef<PickedMention[]>(start.picked);
  const area = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<{ q: string; at: number } | null>(null);
  const [active, setActive] = useState(0);
  /** Where the caret goes once a picked mention is in the text (set as React commits it, before the next keystroke). */
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (caret.current === null || !area.current) return;
    area.current.focus();
    area.current.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [text]);

  const update = (t: string) => {
    setText(t);
    onTextChange?.(t);
  };

  useImperativeHandle(ref, () => ({
    value: () => encodeMentions(text, picked.current),
    reset: (body = '') => {
      const d = decodeMentions(body);
      picked.current = d.picked;
      update(d.text);
      setQuery(null);
    },
    focus: () => area.current?.focus(),
  }));

  const matches = useMemo(() => {
    if (!query) return [];
    const q = query.q.toLowerCase();
    return candidates
      .filter((c) => !q || c.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)) || c.name.toLowerCase().startsWith(q) || c.email?.toLowerCase().startsWith(q))
      .slice(0, 6);
  }, [query, candidates]);

  /** Finds an "@query" right before the caret. */
  const detect = (el: HTMLTextAreaElement) => {
    const before = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = /(^|\s)@([^\s@]{0,30})$/.exec(before);
    if (m) {
      setQuery({ q: m[2], at: before.length - m[2].length - 1 });
      setActive(0);
    } else setQuery(null);
  };

  const pick = (c: MentionCandidate) => {
    const el = area.current;
    if (!el || !query) return;
    const end = el.selectionStart ?? text.length;
    const insert = `@${c.name} `;
    const next = text.slice(0, query.at) + insert + text.slice(end);
    picked.current = [...picked.current.filter((p) => p.userId !== c.userId), { userId: c.userId, name: c.name }];
    caret.current = query.at + insert.length;
    update(next);
    setQuery(null);
  };

  return (
    <div className="relative">
      <Textarea
        {...rest}
        ref={area}
        className={className}
        value={text}
        onChange={(e) => {
          update(e.target.value);
          detect(e.target);
        }}
        onClick={(e) => detect(e.currentTarget)}
        onBlur={(e) => {
          // Let a click on a suggestion land first.
          setTimeout(() => setQuery(null), 150);
          rest.onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (query && matches.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              e.stopPropagation();
              setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
              return;
            }
            if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'Tab') {
              e.preventDefault();
              e.stopPropagation();
              pick(matches[active]);
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setQuery(null);
              return;
            }
          }
          onKeyDown?.(e);
        }}
      />
      {query && matches.length ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-line bg-panel-2 py-1 shadow-xl" role="listbox" data-mention-list>
          {matches.map((c, i) => (
            <button
              key={c.userId}
              type="button"
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(c)}
              className={cx('flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm', i === active ? 'bg-panel-3' : 'hover:bg-panel-3')}
            >
              <Avatar name={c.name} url={c.avatarUrl} size={20} />
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
              {c.email ? <span className="truncate text-[11px] text-faint">{c.email}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
});
